import { useState, useEffect } from 'react';

/**
 * Custom hook for PWA installation functionality
 * Handles beforeinstallprompt event and provides install methods
 */
export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Detect iOS devices
    const checkIsIOS = () => {
      const ua = window.navigator.userAgent;
      const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      setIsIOS(isIOSDevice);
      return isIOSDevice;
    };

    const iosDevice = checkIsIOS();

    // Enhanced install detection with detailed logging
    const checkInstalled = () => {
      // Method 1: Check display-mode
      const standaloneMatch = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;

      // Method 2: Check iOS Safari standalone mode
      const iosStandalone = window.navigator && window.navigator.standalone;

      // Method 3: Check localStorage
      const localStorageInstalled = localStorage.getItem('pwa-installed') === 'true';

      // Method 4: Check minimal-ui
      const minimalUiMatch = window.matchMedia && window.matchMedia('(display-mode: minimal-ui)').matches;

      const isInstalled = standaloneMatch || iosStandalone || localStorageInstalled || minimalUiMatch;

      if (isInstalled) {
        setIsInstalled(true);
        setCanInstall(false);
        localStorage.setItem('pwa-installed', 'true');
        return true;
      }

      return false;
    };

    // Check on mount
    const installed = checkInstalled();

    if (!installed) {
      // For iOS devices, always show install option (since beforeinstallprompt doesn't fire)
      if (iosDevice) {
        setCanInstall(true);
      } else {
        // For non-iOS, check if was installable before
        const wasInstallable = localStorage.getItem('pwa-installable');
        if (wasInstallable === 'true') {
          setCanInstall(true);
        }
      }
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      //
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
      localStorage.setItem('pwa-installable', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const installApp = async () => {
    // For iOS devices, show instructions instead of trying to prompt
    if (isIOS) {
      showInstallInstructions();
      return false;
    }

    // For non-iOS devices with deferred prompt
    if (!deferredPrompt) {
      showInstallInstructions();
      return false;
    }

    try {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
        setCanInstall(false);
      }

      // Clear the deferredPrompt variable
      setDeferredPrompt(null);
      setCanInstall(false);

      return outcome === 'accepted';
    } catch (error) {
      showInstallInstructions();
      return false;
    }
  };

  const showInstallInstructions = () => {
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    let instructions = '';

    if (isIOSDevice) {
      instructions = '📱 Install Clean City Belmopan on iOS:\n\n1. Tap the Share button 📤 at the bottom of Safari\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" in the top right\n\nThe app icon will appear on your home screen!';
    } else if (isAndroid) {
      instructions = '📱 Install Clean City Belmopan on Android:\n\n1. Tap the menu button (⋮) in the top right\n2. Tap "Install app" or "Add to Home screen"\n3. Tap "Install" to confirm\n\nThe app will be added to your device!';
    } else if (isSafari) {
      instructions = '💻 Install Clean City Belmopan on Mac/Desktop:\n\n1. Click File menu → Add to Dock\nOR\n1. Look for the install icon (⊕) in the address bar\n2. Click it and follow the prompts';
    } else {
      instructions = '💻 Install Clean City Belmopan:\n\n1. Look for the install icon in your browser address bar\n2. Click it and follow the prompts\nOR\n1. Use your browser menu → Install or Add to desktop';
    }

    alert(instructions);
  };

  return {
    canInstall,
    isInstalled,
    installApp,
    showInstallInstructions,
    isIOS
  };
};